import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const Equipment = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="equipment-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="equipment-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=1920&q=80"
            alt="Equipment & Capabilities"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-blue-900/85"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="equipment-heading">
            Equipment & Capabilities
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            State-of-the-art equipment and certified processes ensuring precision and quality in every project.
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="equipment-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Link
              to="/equipment/list"
              className="group bg-white rounded-2xl border-2 border-gray-100 p-8 card-hover"
              data-testid="equipment-list-card"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 heading-font mb-4">Equipment List</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Explore our comprehensive inventory of cutting-edge manufacturing equipment including CNC machines, welding equipment, cutting systems, and more.
                </p>
              </div>
              <div className="flex items-center text-blue-700 font-semibold group-hover:text-blue-800">
                View Equipment List
                <ArrowRight className="ml-2 w-5 h-5" />
              </div>
            </Link>

            <Link
              to="/equipment/certification"
              className="group bg-white rounded-2xl border-2 border-gray-100 p-8 card-hover"
              data-testid="certification-card"
            >
              <div className="mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 heading-font mb-4">Quality Certification</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Learn about our quality certifications, standards compliance, and the rigorous processes that ensure excellence in every project.
                </p>
              </div>
              <div className="flex items-center text-blue-700 font-semibold group-hover:text-blue-800">
                View Certifications
                <ArrowRight className="ml-2 w-5 h-5" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="equipment-highlights">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 heading-font mb-4">Our Capabilities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Advanced equipment and expert craftsmen working together to deliver precision results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="capability-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 heading-font">CNC Machining</h3>
              <p className="text-sm text-gray-600">Multi-axis CNC mills, lathes, and EDM machines for precision components</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="capability-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 heading-font">Metal Fabrication</h3>
              <p className="text-sm text-gray-600">Laser cutting, plasma cutting, forming, and certified welding capabilities</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="capability-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 heading-font">Testing & Inspection</h3>
              <p className="text-sm text-gray-600">Hydrostatic testing, NDE, and CMM inspection for quality assurance</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Equipment;